import { X, Play, Edit, MapPin, Tag, Info } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AudioPin } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';

type YouTubePlayerProps = {
  pin: AudioPin;
  pins: AudioPin[];
  onClose: () => void;
  onPlay?: () => void;
  onEdit?: () => void;
  onPinSelect?: (pin: AudioPin) => void;
  canEdit?: boolean;
};

export function YouTubePlayer({ pin, pins, onClose, onPlay, onEdit, onPinSelect, canEdit }: YouTubePlayerProps) {
  const { language } = useLanguage();
  const [currentPin, setCurrentPin] = useState(pin);
  const [isPlaying, setIsPlaying] = useState(false);
  const [embedError, setEmbedError] = useState(false);
  const [playerKey, setPlayerKey] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const thumbnailUrl = `https://img.youtube.com/vi/${currentPin.youtube_video_id}/maxresdefault.jpg`;
  const embedUrl = `https://www.youtube-nocookie.com/embed/${currentPin.youtube_video_id}?autoplay=1&enablejsapi=1&playsinline=1&rel=0&modestbranding=1`;
  const youtubeUrl = `https://www.youtube.com/watch?v=${currentPin.youtube_video_id}`;

  // Handle ESC key to close player
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Update currentPin when pin changes (different pin selected or same pin updated)
  useEffect(() => {
    const pinChanged = pin.id !== currentPin.id;
    const pinUpdated = pin.id === currentPin.id && (
      pin.title !== currentPin.title ||
      pin.description !== currentPin.description ||
      pin.youtube_video_id !== currentPin.youtube_video_id ||
      (pin as any)._timestamp !== (currentPin as any)._timestamp
    );

    if (pinChanged || pinUpdated) {
      if (pinChanged) {
        setIsTransitioning(true);
        setIsPlaying(false);
        setEmbedError(false);

        // Wait for iframe to be fully removed (300ms)
        const timer = setTimeout(() => {
          setCurrentPin(pin);
          setPlayerKey(prev => prev + 1);
          setIsTransitioning(false);
        }, 300);

        return () => clearTimeout(timer);
      } else {
        // Just update the data without transitioning
        setCurrentPin(pin);
      }
    }
  }, [pin, currentPin]);

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay?.();
  };

  const handlePinSelect = (selectedPin: AudioPin) => {
    setIsTransitioning(true);
    setIsPlaying(false);
    setEmbedError(false);

    // Wait for iframe to be fully removed (300ms)
    const timer = setTimeout(() => {
      setCurrentPin(selectedPin);
      setPlayerKey(prev => prev + 1);
      setIsTransitioning(false);
      onPinSelect?.(selectedPin);
    }, 300);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl z-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-start gap-4">
          {pins.length > 1 && (
            <div className="w-64 flex-shrink-0 max-h-96 overflow-y-auto bg-gray-50 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {language === 'ko' ? `이 위치의 음악 (${pins.length}개)` : `Music at this location (${pins.length})`}
              </h4>
              <div className="space-y-2">
                {pins.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handlePinSelect(p)}
                    className={`w-full text-left p-2 rounded-lg transition-all ${
                      currentPin.id === p.id
                        ? 'bg-purple-100 border-2 border-purple-500'
                        : 'bg-white border border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {(p as any).source === 'ai' && (
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-1 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.title}</p>
                        {(p as any).ai_reason && (
                          <p className="text-xs text-purple-600 mt-0.5 line-clamp-2">{(p as any).ai_reason}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-xl truncate">{currentPin.title}</h3>
                  {(currentPin as any).source === 'ai' && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                      AI
                    </span>
                  )}
                </div>

                {currentPin.description && (
                  <div className="flex items-start gap-2 mb-2">
                    <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600 line-clamp-2">{currentPin.description}</p>
                  </div>
                )}

                {currentPin.location_name && (
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <p className="text-sm text-gray-600">{currentPin.location_name}</p>
                  </div>
                )}

                {currentPin.tags && currentPin.tags.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {currentPin.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 bg-teal-100 text-blue-700 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(currentPin as any).ai_reason && (
                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-xs text-purple-700">
                      <span className="font-semibold">
                        {language === 'ko' ? 'AI 추천 이유: ' : 'AI Reason: '}
                      </span>
                      {(currentPin as any).ai_reason}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {canEdit && onEdit && (
                  <button
                    onClick={onEdit}
                    className="p-2 text-[#00c7b7] hover:bg-teal-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="w-full max-w-4xl">
              <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
                {isTransitioning ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-900">
                    <div className="text-center text-white">
                      <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-sm">{language === 'ko' ? '전환 중...' : 'Loading...'}</p>
                    </div>
                  </div>
                ) : !isPlaying ? (
                  <div className="relative w-full h-full">
                    <img
                      src={thumbnailUrl}
                      alt={currentPin.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://img.youtube.com/vi/${currentPin.youtube_video_id}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <div className="flex flex-col items-center gap-4">
                        <button
                          onClick={handlePlay}
                          className="bg-red-600 rounded-full w-20 h-20 flex items-center justify-center hover:scale-110 transition-transform hover:bg-red-700"
                        >
                          <div className="w-0 h-0 border-t-[15px] border-t-transparent border-l-[25px] border-l-white border-b-[15px] border-b-transparent ml-1"></div>
                        </button>
                        <a
                          href={youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-white/90 hover:bg-white text-gray-900 font-semibold rounded-lg transition-colors text-sm flex items-center gap-2"
                        >
                          <Play className="w-4 h-4" />
                          {language === 'ko' ? 'YouTube에서 보기' : 'Watch on YouTube'}
                        </a>
                      </div>
                    </div>
                  </div>
                ) : embedError ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white p-8">
                    <div className="text-center mb-6">
                      <div className="bg-red-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                        <Play className="w-10 h-10" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">
                        {language === 'ko' ? '임베드 재생 불가' : 'Embedding Disabled'}
                      </h3>
                      <p className="text-gray-300 text-sm mb-6">
                        {language === 'ko'
                          ? '이 동영상은 외부 사이트에서 재생할 수 없습니다.'
                          : 'This video cannot be played on external sites.'}
                      </p>
                    </div>
                    <a
                      href={youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Play className="w-5 h-5" />
                      {language === 'ko' ? 'YouTube에서 보기' : 'Watch on YouTube'}
                    </a>
                  </div>
                ) : (
                  <iframe
                    key={`youtube-${currentPin.id}-${playerKey}`}
                    src={embedUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    title={currentPin.title}
                    onError={() => setEmbedError(true)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
